import { useEffect, useRef } from "react";
import { isEditableTarget } from "../command/useGlobalShortcuts";

// Release 1.5.0 keyboard layer (rec. #10). Ctrl/⌘+K search · Ctrl/⌘+P and Ctrl/⌘+Shift+P Go to · "g" then h / w / g / k / f
// to jump · K, F, R (D-O76) · "/" the page's own filter · "?" the cheat sheet. Letters and chords never fire while the
// person types in a field or while a dialog is open; the Ctrl combinations work everywhere. On the work-order router
// document Ctrl+P is left to the browser's print.

export const CHORD_TIMEOUT_MS = 1200;

export type ChordTarget = "hub" | "work-orders" | "gantt" | "kanban" | "floor-command";

export const CHORDS: Readonly<Record<string, ChordTarget>> = { h: "hub", w: "work-orders", g: "gantt", k: "kanban", f: "floor-command" };

export const SINGLE_KEYS: Readonly<Record<string, ChordTarget | "scenarios">> = { k: "kanban", f: "floor-command", r: "scenarios" };

export interface HeaderShortcutHandlers {
  enabled: boolean;
  onSearch: () => void;
  onGoTo: () => void;
  onCheatSheet: () => void;
  onJump: (target: ChordTarget | "scenarios") => void;
  onPageFilter: () => void;
  /** True when Ctrl+P must print here (the browser keeps it). */
  ctrlPPrints: () => boolean;
}

function dialogOpen(): boolean {
  return typeof document !== "undefined" && document.querySelector("[role='dialog'], [role='alertdialog'], dialog[open]") !== null;
}

export function useHeaderShortcuts(h: HeaderShortcutHandlers): void {
  const handlers = useRef(h);
  handlers.current = h;

  useEffect(() => {
    if (!h.enabled) return;
    let pendingG = 0;

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const hs = handlers.current;
      const key = e.key.toLowerCase();

      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        if (key === "k" && !e.shiftKey) {
          e.preventDefault();
          hs.onSearch();
        } else if (key === "p") {
          if (!e.shiftKey && hs.ctrlPPrints()) return;
          e.preventDefault();
          hs.onGoTo();
        }
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) {
        pendingG = 0;
        return;
      }

      if (e.key === "?") {
        e.preventDefault();
        hs.onCheatSheet();
        return;
      }
      if (dialogOpen()) {
        pendingG = 0;
        return;
      }

      if (pendingG && Date.now() - pendingG <= CHORD_TIMEOUT_MS) {
        pendingG = 0;
        const target = CHORDS[key];
        if (target) {
          e.preventDefault();
          hs.onJump(target);
        }
        return;
      }
      pendingG = 0;

      if (key === "g" && !e.shiftKey) {
        e.preventDefault();
        pendingG = Date.now();
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        hs.onPageFilter();
        return;
      }
      const single = SINGLE_KEYS[key];
      if (single && !e.shiftKey) {
        e.preventDefault();
        hs.onJump(single);
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [h.enabled]);
}

export interface ShortcutRow {
  keys: string[];
  description: string;
}

/** The "?" cheat sheet, in the order a person learns them. */
export const HEADER_SHORTCUTS: readonly ShortcutRow[] = [
  { keys: ["Ctrl", "K"], description: "Search work orders, parts, customers, machines, purchase orders, knowledge and pages" },
  { keys: ["Ctrl", "P"], description: "Go to a page, an action or a recent record (also Ctrl+Shift+P)" },
  { keys: ["g", "h"], description: "Your start page (first hub)" },
  { keys: ["g", "w"], description: "Work orders" },
  { keys: ["g", "g"], description: "Order Gantt" },
  { keys: ["g", "k"], description: "Shop Kanban (or K)" },
  { keys: ["g", "f"], description: "Floor Command (or F)" },
  { keys: ["R"], description: "Scenario lab and Rebalance" },
  { keys: ["/"], description: "The filter on this page (or search when the page has none)" },
  { keys: ["?"], description: "Show or hide this list" },
  { keys: ["Esc"], description: "Close a panel or dialog" }
];
